import React, { useMemo, useState } from 'react';
import { AvTag, TextInput } from '@fabric-ui/components';
import { Search } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import useQueryObject from '@hooks/useQueryObjectSql';
import { Box, Divider, Skeleton, Typography } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { calculatePercentageChange } from '../utils';
import { getAlertsByAgent, getAllAgents } from './queries';
import StatusChange from './statusChange';
import { agentDescription, agentWidgetBoxStyle, getLast24HoursDate } from './utils';

const AgentTiles = ({ filterContext }) => {
  const [searchValue, setSearchValue] = useState('');

  const { data: totalAlertsByAgents, isLoading: isTotalAlertsByAgentsDataLoading } = useQueryObject({
    queryObject: [getAlertsByAgent(filterContext)],
    options: { placeholderData: keepPreviousData },
  });

  const { data: totalAlertsByAgentsInLast24Hours, isLoading: isTotalAlertsByAgentsInLast24HoursDataLoading } = useQueryObject({
    queryObject: [
      getAlertsByAgent({
        ...filterContext,
        dateRange: [getLast24HoursDate(filterContext.dateRange[1]), filterContext.dateRange[1]],
      }),
    ],
    options: { placeholderData: keepPreviousData },
  });

  const totalAlertsByAgentsInLast24HoursMap = useMemo(() => {
    if (!isTotalAlertsByAgentsInLast24HoursDataLoading) {
      return new Map(totalAlertsByAgentsInLast24Hours?.map(a => [a.agent_id, a.cnt]));
    }
    return new Map();
  }, [totalAlertsByAgentsInLast24Hours, isTotalAlertsByAgentsInLast24HoursDataLoading]);

  const { data: totalAgents, isLoading: isTotalAgentsDataLoading } = useQueryObject({
    queryObject: [getAllAgents],
    options: { placeholderData: keepPreviousData },
  });

  const AgentTilesData = useMemo(() => {
    if (isTotalAgentsDataLoading || isTotalAlertsByAgentsDataLoading) return [];

    const alertsMap = new Map(totalAlertsByAgents?.map(a => [a.agent_id, a.cnt]));
    const alertsOutcomeMap = new Map(totalAlertsByAgents?.map(a => [a.agent_id, a.outcome]));

    return (totalAgents || [])
      .map((agent: any) => ({
        ...agent,
        cnt: alertsMap.get(agent.agent_id) ?? 0,
        outcome: alertsOutcomeMap.get(agent.agent_id) ?? '',
      }))
      .sort((a, b) => b.cnt - a.cnt);
  }, [totalAgents, totalAlertsByAgents, isTotalAgentsDataLoading, isTotalAlertsByAgentsDataLoading]);

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Typography variant="h5" sx={{ mb: 1, mt: 1, color: 'var(--color-content-base-primary)' }}>
          Agent Fleet - Digital Workforce
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--color-content-base-secondary)' }}>
          Click on any agent to view detailed alert history and investigations
        </Typography>
        <TextInput
          size="small"
          helperText="Search Agent"
          icon={<Search />}
          value={searchValue}
          onChange={value => setSearchValue(value)}
          sx={{ width: '450px', mb: 2, mt: 2 }}
        />
      </Box>
      <Box sx={{ ...flex.row, flexWrap: 'wrap', gap: 2, width: '100%' }}>
        {!isTotalAgentsDataLoading && !isTotalAlertsByAgentsDataLoading && !isTotalAlertsByAgentsInLast24HoursDataLoading
          ? AgentTilesData.filter(agent => (agent.name || '').toLowerCase().includes(searchValue.toLowerCase())).map(
              ({ agent_id, name, description, cnt, category, outcome }) => (
                <Box sx={agentWidgetBoxStyle} key={agent_id}>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: '16px', minHeight: '74px', p: 2, pb: 1, color: 'var(--color-content-base-primary)' }}>
                    {name}
                  </Typography>
                  <AvTag sx={{ m: 2, mt: 0 }} variant="primary" text={category} oneLine />
                  <Divider sx={{ borderColor: 'var(--color-border-base-subdued)' }} />
                  <Box sx={{ ...flex.row, p: 2, justifyContent: 'space-between' }}>
                    <Typography variant="body1" sx={{ color: 'var(--color-content-base-secondary)' }}>
                      Alerts investigated
                    </Typography>
                    <Box sx={{ ...flex.row, gap: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: '16px', color: 'var(--color-content-base-primary)' }}>
                        {cnt}
                      </Typography>
                      <StatusChange value={calculatePercentageChange(totalAlertsByAgentsInLast24HoursMap.get(agent_id) || 0, cnt)} />
                    </Box>
                  </Box>
                  <Divider sx={{ borderColor: 'var(--color-border-base-subdued)' }} />
                  <Typography variant="body2" sx={agentDescription}>
                    {description}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1, p: 2, pb: 0 }}>
                    {outcome && (
                      <>
                        <Typography variant="body2">Agent Outcome</Typography>
                        <AvTag variant="neutral" text={outcome} oneLine />
                      </>
                    )}
                  </Box>
                </Box>
              )
            )
          : Array.from({ length: 4 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={`agent-tile-${index}`} sx={agentWidgetBoxStyle}>
                <Skeleton variant="rectangular" width="100%" height="250px" sx={{ borderRadius: '2px' }} />
              </Box>
            ))}
      </Box>
    </>
  );
};

export default AgentTiles;
